import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Tooltip,
  InputAdornment,
  MenuItem,
  Grid2 as Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Help as HelpIcon,
  Search as SearchIcon,
  ExpandMore as ExpandMoreIcon,
  ViewList as ListIcon,
  ViewModule as GridIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { cmsService, CMSFAQ, CMSPage } from '../../services';

const CMSFAQPAGE: React.FC = () => {
  const { t } = useTranslation();
  const [faqs, setFaqs] = useState<CMSFAQ[]>([]);
  const [pages, setPages] = useState<CMSPage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState<'table' | 'accordion'>('table');
  const [categoryFilter, setCategoryFilter] = useState<string>('');

  const [editDialog, setEditDialog] = useState(false);
  const [editData, setEditData] = useState<Partial<CMSFAQ> | null>(null);
  const [saving, setSaving] = useState(false);

  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; item: CMSFAQ | null }>({
    open: false,
    item: null,
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [faqRes, pagesRes] = await Promise.all([
        cmsService.getFAQ(),
        cmsService.getPages(),
      ]);
      setFaqs(faqRes.data.data || []);
      setPages(pagesRes.data.data || []);
    } catch (err: any) {
      setError(err.message || t('cms.errorLoadingFaqs'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Get unique categories
  const categories = Array.from(new Set(faqs.map(f => f.categoria).filter(Boolean))) as string[];

  const handleOpenEdit = (faq?: CMSFAQ) => {
    if (faq) {
      setEditData(faq);
    } else {
      setEditData({
        pergunta: '',
        resposta: '',
        categoria: '',
        ordem: 0,
        status: 'ativo',
        pagina_id: undefined,
      });
    }
    setEditDialog(true);
  };

  const handleSave = async () => {
    if (!editData?.pergunta || !editData?.resposta) {
      setError(t('cms.questionAnswerRequired'));
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const faqData = {
        pagina_id: editData.pagina_id,
        categoria: editData.categoria || '',
        pergunta: editData.pergunta,
        resposta: editData.resposta,
        ordem: editData.ordem || 0,
        status: editData.status || 'ativo',
      };

      if (editData.id) {
        await cmsService.updateFAQ(editData.id, faqData);
        setSuccess(t('cms.faqUpdated'));
      } else {
        await cmsService.createFAQ(faqData);
        setSuccess(t('cms.faqCreated'));
      }
      setEditDialog(false);
      setEditData(null);
      fetchData();
    } catch (err: any) {
      setError(err.message || t('cms.errorSavingFaq'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteDialog.item) return;
    try {
      await cmsService.deleteFAQ(deleteDialog.item.id);
      setSuccess(t('cms.faqDeleted'));
      setDeleteDialog({ open: false, item: null });
      fetchData();
    } catch (err: any) {
      setError(err.message || t('cms.errorDeletingFaq'));
    }
  };

  const filteredFaqs = faqs.filter(f => {
    if (!searchTerm && !categoryFilter) return true;
    const matchesSearch = !searchTerm ||
      f.pergunta.toLowerCase().includes(searchTerm.toLowerCase()) ||
      f.resposta.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = !categoryFilter || f.categoria === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  // Group FAQs by category for accordion view
  const groupedFaqs = filteredFaqs.reduce((acc, faq) => {
    const cat = faq.categoria || t('cms.general');
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(faq);
    return acc;
  }, {} as Record<string, CMSFAQ[]>);

  return (
    <Box p={3}>
      {/* Header */}
      <Box mb={3} display="flex" justifyContent="space-between" alignItems="center">
        <Box>
          <Typography variant="h4" fontWeight="bold">
            {t('cms.faqTitle')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('cms.faqSubtitle')}
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenEdit()}
        >
          {t('cms.newFaq')}
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Search & Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ py: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid size={{ xs: 12, md: 5 }}>
              <TextField
                fullWidth
                size="small"
                placeholder={t('cms.searchFaqs')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                fullWidth
                size="small"
                select
                label={t('common.category')}
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
              >
                <MenuItem value="">{t('common.allFemale')}</MenuItem>
                {categories.map(cat => (
                  <MenuItem key={cat} value={cat}>{cat}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <Box display="flex" justifyContent="flex-end" gap={1}>
                <Tooltip title={t('cms.tableView')}>
                  <IconButton
                    onClick={() => setViewMode('table')}
                    color={viewMode === 'table' ? 'primary' : 'default'}
                  >
                    <ListIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title={t('cms.accordionView')}>
                  <IconButton
                    onClick={() => setViewMode('accordion')}
                    color={viewMode === 'accordion' ? 'primary' : 'default'}
                  >
                    <GridIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Stats */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 6, md: 3 }}>
          <Card>
            <CardContent sx={{ py: 2 }}>
              <Typography variant="body2" color="text.secondary">{t('cms.totalFaqs')}</Typography>
              <Typography variant="h4" fontWeight="bold">{faqs.length}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <Card>
            <CardContent sx={{ py: 2 }}>
              <Typography variant="body2" color="text.secondary">{t('cms.categories')}</Typography>
              <Typography variant="h4" fontWeight="bold">{categories.length}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <Card>
            <CardContent sx={{ py: 2 }}>
              <Typography variant="body2" color="text.secondary">{t('cms.activeFemale')}</Typography>
              <Typography variant="h4" fontWeight="bold" color="success.main">
                {faqs.filter(f => f.status === 'ativo').length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Content */}
      {loading ? (
        <Box display="flex" justifyContent="center" py={5}>
          <CircularProgress />
        </Box>
      ) : viewMode === 'table' ? (
        <Paper>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell width={50}>{t('cms.order')}</TableCell>
                  <TableCell>{t('cms.question')}</TableCell>
                  <TableCell>{t('common.category')}</TableCell>
                  <TableCell>{t('cms.page')}</TableCell>
                  <TableCell>{t('common.status')}</TableCell>
                  <TableCell align="right">{t('common.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredFaqs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 5 }}>
                      <HelpIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                      <Typography color="text.secondary">
                        {t('cms.noFaqFound')}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredFaqs.map((faq) => (
                    <TableRow key={faq.id} hover>
                      <TableCell>
                        <Chip size="small" label={faq.ordem} variant="outlined" />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {faq.pergunta}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{
                          display: 'block',
                          maxWidth: 400,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}>
                          {faq.resposta}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {faq.categoria ? (
                          <Chip size="small" label={faq.categoria} variant="outlined" />
                        ) : '-'}
                      </TableCell>
                      <TableCell>
                        {faq.pagina_titulo ? (
                          <Chip size="small" label={faq.pagina_titulo} variant="outlined" />
                        ) : (
                          <Chip size="small" label={t('cms.global')} color="info" variant="outlined" />
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          color={faq.status === 'ativo' ? 'success' : 'default'}
                          label={faq.status === 'ativo' ? t('common.active') : t('common.inactive')}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title={t('common.edit')}>
                          <IconButton size="small" onClick={() => handleOpenEdit(faq)}>
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={t('common.delete')}>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => setDeleteDialog({ open: true, item: faq })}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      ) : (
        // Accordion View
        <Box>
          {Object.keys(groupedFaqs).length === 0 ? (
            <Paper sx={{ p: 5, textAlign: 'center' }}>
              <HelpIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
              <Typography color="text.secondary">
                {t('cms.noFaqFound')}
              </Typography>
            </Paper>
          ) : (
            Object.entries(groupedFaqs).map(([category, categoryFaqs]) => (
              <Box key={category} mb={3}>
                <Typography variant="h6" fontWeight="bold" mb={2}>
                  {category}
                </Typography>
                {categoryFaqs.map((faq) => (
                  <Accordion key={faq.id}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Box display="flex" alignItems="center" gap={1} width="100%">
                        <Typography fontWeight="medium" flex={1}>
                          {faq.pergunta}
                        </Typography>
                        <Chip
                          size="small"
                          color={faq.status === 'ativo' ? 'success' : 'default'}
                          label={faq.status === 'ativo' ? t('common.active') : t('common.inactive')}
                        />
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Typography color="text.secondary" mb={2}>
                        {faq.resposta}
                      </Typography>
                      <Box display="flex" gap={1}>
                        <Button
                          size="small"
                          startIcon={<EditIcon />}
                          onClick={() => handleOpenEdit(faq)}
                        >
                          {t('common.edit')}
                        </Button>
                        <Button
                          size="small"
                          color="error"
                          startIcon={<DeleteIcon />}
                          onClick={() => setDeleteDialog({ open: true, item: faq })}
                        >
                          {t('common.delete')}
                        </Button>
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                ))}
              </Box>
            ))
          )}
        </Box>
      )}

      {/* Edit Dialog */}
      <Dialog open={editDialog} onClose={() => setEditDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {editData?.id ? t('cms.editFaq') : t('cms.newFaq')}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid size={12}>
              <TextField
                fullWidth
                label={t('cms.question')}
                value={editData?.pergunta || ''}
                onChange={(e) => setEditData(prev => ({ ...prev, pergunta: e.target.value }))}
                required
                placeholder={t('cms.questionPlaceholder')}
              />
            </Grid>
            <Grid size={12}>
              <TextField
                fullWidth
                multiline
                rows={4}
                label={t('cms.answer')}
                value={editData?.resposta || ''}
                onChange={(e) => setEditData(prev => ({ ...prev, resposta: e.target.value }))}
                required
                placeholder={t('cms.answerPlaceholder')}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label={t('common.category')}
                value={editData?.categoria || ''}
                onChange={(e) => setEditData(prev => ({ ...prev, categoria: e.target.value }))}
                placeholder={t('cms.categoryPlaceholder')}
                helperText={t('cms.categoryHelperText')}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                select
                label={t('cms.page')}
                value={editData?.pagina_id || ''}
                onChange={(e) => setEditData(prev => ({ ...prev, pagina_id: e.target.value ? Number(e.target.value) : undefined }))}
                helperText={t('cms.emptyForGlobal')}
              >
                <MenuItem value="">{t('cms.globalAllPages')}</MenuItem>
                {pages.map((page) => (
                  <MenuItem key={page.id} value={page.id}>{page.titulo}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                select
                label={t('common.status')}
                value={editData?.status || 'ativo'}
                onChange={(e) => setEditData(prev => ({ ...prev, status: e.target.value as 'ativo' | 'inativo' }))}
              >
                <MenuItem value="ativo">{t('common.active')}</MenuItem>
                <MenuItem value="inativo">{t('common.inactive')}</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                type="number"
                label={t('cms.order')}
                value={editData?.ordem || 0}
                onChange={(e) => setEditData(prev => ({ ...prev, ordem: parseInt(e.target.value) || 0 }))}
                helperText={t('cms.orderHelperText')}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialog(false)}>{t('common.cancel')}</Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={saving || !editData?.pergunta || !editData?.resposta}
          >
            {saving ? t('common.saving') : editData?.id ? t('common.save') : t('common.create')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, item: null })}
      >
        <DialogTitle>{t('cms.deleteFaq')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('cms.confirmDeleteFaq', { name: deleteDialog.item?.pergunta })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, item: null })}>
            {t('common.cancel')}
          </Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            {t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CMSFAQPAGE;
